import { useLayoutEffect, useRef } from 'react';

/**
 * Sign-in panel film: "falling into step". Five people each ride their own
 * line, every line moving to a different rhythm. One by one the rhythms match,
 * the lines draw together into one, and everyone ends up evenly spaced on that
 * single line around the client in the middle. The line then slows to a
 * barely moving sway. Everything is kept faint so it never competes with the
 * form. It is one small SVG redrawn each frame: nothing to download.
 *
 * `pictureAt` is the single source for both the markup and the film, so the
 * first render already shows the finished ribbon. With reduced motion that
 * picture simply stays put.
 */

const FRAME = 800;
const MIDDLE_Y = 400;
const SHARED_WAVELENGTH = 360;
const SHARED_AMPLITUDE = 11;
const SHARED_SPEED = 0.8;
// Once in step the line eases down to this share of its speed and stays there
const RESTING_SPEED_SHARE = 0.12;
const SETTLE_SECONDS = 1.4;
const PATH_STEP = 12;

// Each person's own line and rhythm before they fall into step. startX/endX
// stay inside the middle of the frame so nobody is cropped on a narrow panel.
const LINES = [
  { base: 290, wavelength: 210, amplitude: 22, speed: 0.9, phase: 0.4, startX: 300, endX: 230 },
  { base: 345, wavelength: 330, amplitude: 14, speed: -0.6, phase: 2.1, startX: 540, endX: 570 },
  { base: 400, wavelength: 260, amplitude: 26, speed: 1.3, phase: 4.0, startX: 400, endX: 400, isClient: true },
  { base: 455, wavelength: 400, amplitude: 18, speed: 0.7, phase: 1.2, startX: 250, endX: 315 },
  { base: 510, wavelength: 290, amplitude: 24, speed: -1.1, phase: 5.3, startX: 500, endX: 485 },
];

// Film timings, in seconds
const APPEAR_STEP = 0.18;
const SYNC_AT = 3;
const SYNC_STEP = 0.25;
const SYNC_DURATION = 1.6;
const SYNCED_AT = SYNC_AT + (LINES.length - 1) * SYNC_STEP + SYNC_DURATION;
const MESSAGE_AT = SYNCED_AT - 0.2;
const HELD_AT = SYNCED_AT + 6;

// The five lines end up on top of each other, so each is fainter once together
const LINE_OPACITY = { apart: 0.13, together: 0.07 };
const PERSON_OPACITY = { team: 0.36, client: 0.55 };

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const ramp = (t, start, end) => clamp((t - start) / (end - start), 0, 1);
const mix = (from, to, amount) => from + (to - from) * amount;
const easeOut = (p) => 1 - (1 - p) ** 3;
const easeInOut = (p) => (p < 0.5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2);

const syncAmount = (index, t) => easeInOut(ramp(t, SYNC_AT + index * SYNC_STEP, SYNC_AT + index * SYNC_STEP + SYNC_DURATION));

// Clock for the shared wave: real time until everyone is in step, then it
// eases down to the resting speed without a jump.
function sharedClock(t) {
  if (t <= SYNCED_AT) return t;
  const since = t - SYNCED_AT;
  const easedOff = SETTLE_SECONDS * (1 - Math.exp(-since / SETTLE_SECONDS));
  return SYNCED_AT + RESTING_SPEED_SHARE * since + (1 - RESTING_SPEED_SHARE) * easedOff;
}

function lineY(index, x, t) {
  const line = LINES[index];
  const synced = syncAmount(index, t);
  const ownAngle = (2 * Math.PI * x) / line.wavelength + line.phase - line.speed * t;
  const sharedAngle = (2 * Math.PI * x) / SHARED_WAVELENGTH - SHARED_SPEED * sharedClock(t);
  const base = mix(line.base, MIDDLE_Y, synced);
  return base + mix(line.amplitude, SHARED_AMPLITUDE, synced) * Math.sin(mix(ownAngle, sharedAngle, synced));
}

function linePath(index, t) {
  let d = '';
  for (let x = -PATH_STEP; x <= FRAME + PATH_STEP; x += PATH_STEP) {
    d += `${d ? 'L' : 'M'}${x} ${lineY(index, x, t).toFixed(1)}`;
  }
  return d;
}

/** Everything the panel shows at time t (seconds since the film started). */
function pictureAt(t) {
  const lines = LINES.map((line, index) => {
    const synced = syncAmount(index, t);
    const appear = easeOut(ramp(t, index * APPEAR_STEP, index * APPEAR_STEP + 0.8));
    const x = mix(line.startX, line.endX, synced);
    return {
      d: linePath(index, t),
      lineOpacity: appear * mix(LINE_OPACITY.apart, LINE_OPACITY.together, synced),
      personOpacity: appear * (line.isClient ? PERSON_OPACITY.client : PERSON_OPACITY.team),
      personTransform: `translate(${x.toFixed(1)},${lineY(index, x, t).toFixed(1)})`,
    };
  });

  return {
    lines,
    openingOpacity: 1 - ramp(t, MESSAGE_AT - 0.5, MESSAGE_AT),
    closingOpacity: easeOut(ramp(t, MESSAGE_AT, MESSAGE_AT + 0.7)),
  };
}

const HELD_PICTURE = pictureAt(HELD_AT);

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Person({ isClient }) {
  return (
    <g transform={`scale(${isClient ? 1.25 : 0.9})`}>
      <circle r="13" className="fill-slate-950" fillOpacity="0.7" stroke="currentColor" strokeWidth="1" />
      <circle cy="-3.4" r="3.6" fill="currentColor" />
      <path d="M-6.5 7.6a6.5 5.6 0 0 1 13 0z" fill="currentColor" />
    </g>
  );
}

export default function LoginMotif() {
  const rootRef = useRef(null);

  // Layout effect: the film's first frame is applied before the browser
  // paints, so the finished picture never flashes ahead of the opening.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return undefined;

    const svg = root.querySelector('svg');
    const paths = Array.from(root.querySelectorAll('.lm-line'));
    const people = Array.from(root.querySelectorAll('.lm-person'));
    const opening = root.querySelector('.lm-opening');
    const closing = root.querySelector('.lm-closing');

    const show = (picture) => {
      picture.lines.forEach((line, index) => {
        paths[index].setAttribute('d', line.d);
        paths[index].setAttribute('opacity', line.lineOpacity.toFixed(3));
        people[index].setAttribute('transform', line.personTransform);
        people[index].setAttribute('opacity', line.personOpacity.toFixed(3));
      });
      opening.style.opacity = picture.openingOpacity;
      closing.style.opacity = picture.closingOpacity;
    };

    let startedAt = null;
    let frameId = 0;
    const frame = (now) => {
      if (startedAt === null) startedAt = now;
      // Skip the work while the panel is hidden (phones and tablets)
      if (svg.clientWidth > 0) show(pictureAt((now - startedAt) / 1000));
      frameId = requestAnimationFrame(frame);
    };
    show(pictureAt(0));
    frameId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(frameId);
      show(HELD_PICTURE);
    };
  }, []);

  return (
    <div ref={rootRef} className="absolute inset-0 z-10" aria-hidden="true">
      <svg viewBox={`0 0 ${FRAME} ${FRAME}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full text-teal-200" fill="none">
        {HELD_PICTURE.lines.map((line, index) => (
          <path
            key={`line-${LINES[index].base}`}
            className={`lm-line ${LINES[index].isClient ? 'text-emerald-300' : ''}`}
            d={line.d}
            stroke="currentColor"
            strokeWidth="1.1"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={line.lineOpacity}
          />
        ))}
        {HELD_PICTURE.lines.map((line, index) => (
          <g
            key={`person-${LINES[index].base}`}
            className={`lm-person ${LINES[index].isClient ? 'text-emerald-300' : ''}`}
            transform={line.personTransform}
            opacity={line.personOpacity}
          >
            <Person isClient={LINES[index].isClient} />
          </g>
        ))}
      </svg>

      <div className="absolute bottom-10 left-10 right-10">
        <div className="relative h-6 text-sm tracking-wide text-white/60">
          <div className="lm-opening absolute inset-x-0 top-0" style={{ opacity: HELD_PICTURE.openingOpacity }}>
            Many people. Many moving parts.
          </div>
          <div className="lm-closing absolute inset-x-0 top-0" style={{ opacity: HELD_PICTURE.closingOpacity }}>
            Everyone moving together.
          </div>
        </div>
        <div className="mt-1 text-2xl font-medium text-white">Care, in sync.</div>
      </div>
    </div>
  );
}
